import { registerActions, getAction } from "../actionRegistry";
import { toastSuccess, toastError } from "../ui/messages";
import { modal, closeModalLast } from "../ui/modal";
import { getRandomInt } from "../utils/string";
import { api } from "../api";
import { parseOptions } from "./miscOperations";
import { enterReorderMode } from "./collections";
import { multiAddDownloadcart } from "./cart";
import {
  getDomElement,
  setText,
  setHtml,
  setStyle,
  show,
  hide,
  queryAll,
  idSuffix,
  fadeOut,
} from "../utils/dom";
import {
  load_active,
  loadmoretimer,
  reordermode,
  setLoadmoretimer,
  setSearchcache,
} from "../globalState";
import { clearPrintSize } from "./printSizeFilter";

/**
 * The `.select-store` elements ARE the hidden inputs holding each artwork's
 * selected state ("1"/"0"), with id `selected-<artworkId>`. The legacy code
 * round-tripped through `getDomElement($(this).attr("id"))`, which just resolved
 * back to the same element.
 */
function selectStores(): NodeListOf<HTMLInputElement> {
  return queryAll<HTMLInputElement>(".select-store");
}

/** Every currently-selected artwork id, as the raw id-suffix string. */
function selectedIds(): string[] {
  const ids: string[] = [];
  for (const el of selectStores()) {
    if (el.value === "1") ids.push(idSuffix(el, "selected-"));
  }
  return ids;
}

/** Same as selectedIds(), numeric, dropping anything not > 0 (the legacy guard). */
function selectedNumericIds(): number[] {
  return selectedIds()
    .map(Number)
    .filter((n) => n > 0);
}

/** Swap a `#select-icon-*` between empty-square and checked-square. */
function setSelectIcon(check_id: string, checked: boolean): void {
  const icon = getDomElement("select-icon-" + check_id);
  if (!icon) return;
  if (checked) {
    icon.classList.remove("far", "fa-square");
    icon.classList.add("fas", "fa-check-square");
  } else {
    icon.classList.remove("fas", "fa-check-square");
    icon.classList.add("far", "fa-square");
  }
}

/** Fade out each selected artwork tile and clear its stored selection. */
function fadeOutAndDeselectSelected(): void {
  for (const el of selectStores()) {
    if (el.value !== "1") continue;
    fadeOut("img-" + idSuffix(el, "selected-"));
    el.value = "0";
  }
}

export function selectToggle(): void {
  const lmSelect = getDomElement<HTMLInputElement>("lm_select");
  if (!lmSelect) throw new Error("Element not found: #lm_select");
  const selecting = lmSelect.value !== "1";

  if (selecting) {
    setHtml(
      "select-button",
      '<i class="fas fa-hand-pointer"></i> Done<span class="mobile-hide"> selecting</span>'
    );
  } else {
    setHtml("select-button", '<i class="fas fa-hand-pointer"></i> Select');
  }
  lmSelect.value = selecting ? "1" : "0";

  for (const el of queryAll<HTMLElement>("div.select")) {
    el.style.display = selecting ? "block" : "none";
  }
  for (const [selector, hiddenClass] of [
    ["span.overlay-buttons", "overlay-buttons-hidden"],
    ["span.already-downloaded", "already-downloaded-hidden"],
    ["span.in-list", "in-list-hidden"],
    [".view-download-buttons", "view-download-buttons-hidden"],
  ] as const) {
    for (const el of queryAll<HTMLElement>(selector)) {
      el.classList.toggle(hiddenClass, selecting);
    }
  }
  if (selecting) show("select-popup");
  else hide("select-popup");

  selectCounter();
}

export function selectCounter(): void {
  const selected = selectedIds().length;
  setText("select-popup-status-counter", String(selected));
  if (selected === 1) {
    setText("select-popup-status-counter-s", "");
  } else {
    setText("select-popup-status-counter-s", "s");
  }
  setStyle("select-popup-buttons-hide", "opacity", selected > 0 ? "1" : "0.3");
  if (
    getDomElement("lightbox_id") ||
    getDomElement("collection_id") ||
    getDomElement("artist_id")
  ) {
    show("select-popup-buttons-selectall");
  } else {
    hide("select-popup-buttons-selectall");
  }
}

export function deselectall(): void {
  for (const el of selectStores()) {
    if (el.value !== "1") continue;
    el.value = "0";
    setSelectIcon(idSuffix(el, "selected-"), false);
  }
  selectCounter();
}

export function selectall(): void {
  let needload = 0;
  const lmActive = getDomElement<HTMLInputElement>("lm_active");
  const lmMode = getDomElement<HTMLInputElement>("lm_mode");
  const lmTarget = getDomElement<HTMLInputElement>("lm_target");
  const lmInfscrollActive = getDomElement<HTMLInputElement>("lm_infscroll_active");
  const lmInfscrollDiscreet = getDomElement("lm_infscroll_discreet");

  if (load_active === 0 && lmMode && lmTarget) {
    if (getDomElement(lmTarget.value) && lmActive?.value === "1") {
      needload = 1;
    }
  }

  if (lmInfscrollActive && !lmInfscrollDiscreet && lmInfscrollActive.value === "1") {
    needload = 0;
  }

  if (needload !== 0) {
    toastSuccess("Hold on, loading all artworks...");
    clearTimeout(loadmoretimer);
    getAction("load")?.();
    setLoadmoretimer(
      setTimeout(function () {
        selectall();
      }, 2000)
    );
    return;
  }

  for (const el of selectStores()) {
    if (el.value !== "0") continue;

    const check_id = idSuffix(el, "selected-");
    if (getDomElement("infscroll-" + check_id) && !lmInfscrollDiscreet) continue;

    el.value = "1";
    setSelectIcon(check_id, true);
  }
  selectCounter();
}

export async function multimetasave(): Promise<void> {
  const ids = selectedNumericIds();
  if (!ids.length) return;
  const gradeEl = getDomElement<HTMLSelectElement>("multimeta-grade");
  const muralgradeEl = getDomElement<HTMLSelectElement>("multimeta-muralgrade");
  const categoryEl = getDomElement<HTMLSelectElement>("multimeta-category");
  const badprintEl = getDomElement<HTMLSelectElement>("multimeta-badprint");
  const qcEl = getDomElement<HTMLSelectElement>("multimeta-qc");

  try {
    await api.artwork.saveMultiMeta({
      ids,
      grade: gradeEl?.value ?? "",
      muralgrade: muralgradeEl?.value ?? "",
      category: categoryEl?.value ?? "",
      badprint: badprintEl?.value ?? "",
      qc: qcEl?.value ?? "",
    });
    closeModalLast();
    toastSuccess("Metadata set for all artworks");
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export function multimetapreview(): void {
  let html = "";

  for (const check_id of selectedIds()) {
    const imgEl = getDomElement<HTMLImageElement>("imageobject-" + check_id);
    if (!imgEl) continue;
    const src = imgEl.src.replace("ld", "sq");
    html +=
      '<img src="' +
      src +
      '" style="width: 70px; height: 70px; border-radius: 7px; margin: 0px 5px 5px 0px;">';
  }

  setHtml("multimeta-preview", html);
}

export async function multi(what: string): Promise<void> {
  const ids = selectedIds().join(",");

  if (what === "download") {
    void multiAddDownloadcart("ids", ids);
  }
  if (what === "transfer") {
    modal("ecommercesync:artworks:" + ids, "large");
  }
  if (what === "lightbox") {
    modal("multilist:" + ids, "dynamic");
  }
  if (what === "collection") {
    modal("multicollection:" + ids, "dynamic");
  }

  if (what === "stretch") {
    const stretchIds = selectedNumericIds();
    const artworks = stretchIds.length;
    const artworks_s = artworks === 1 ? "" : "s";

    const agree = confirm(
      "Are you sure you want to set the selected " +
        artworks +
        " artwork" +
        artworks_s +
        " to stretch mode?"
    );
    if (!agree) return;
    try {
      await api.artwork.markAsStretch({ photos: stretchIds });
      selectToggle();
      toastSuccess(artworks + " artwork" + artworks_s + " will now be in stretch mode");
    } catch (err: unknown) {
      toastError((err as Error).message);
    }
  }

  if (what === "addtag") {
    const tagIds = selectedNumericIds();

    const multiTagEl = getDomElement<HTMLInputElement>("multi_tag");
    const multitag_add = multiTagEl?.value.toUpperCase() ?? "";

    try {
      const response = await api.artwork.multiTag({ photos: tagIds, tag: multitag_add });
      closeModalLast();
      toastSuccess(
        'Tag "' + multitag_add + '" has been added to ' + response.affected + " artworks"
      );
    } catch (err: unknown) {
      toastError((err as Error).message);
    }
  }

  if (what === "deletetag") {
    const deleteTagIds = selectedNumericIds();

    const multiTagEl = getDomElement<HTMLInputElement>("multi_tag");
    const multitag_add = multiTagEl?.value.toUpperCase() ?? "";

    try {
      const response = await api.artwork.multiDeleteTag({
        photos: deleteTagIds,
        tag: multitag_add,
      });
      closeModalLast();
      toastSuccess(
        'Tag "' + multitag_add + '" has been removed from ' + response.affected + " artworks"
      );
    } catch (err: unknown) {
      toastError((err as Error).message);
    }
  }

  if (what === "badprint") {
    const badprintIds = selectedNumericIds();
    const artworks = badprintIds.length;
    const artworks_s = artworks === 1 ? "" : "s";

    const agree = confirm(
      "Are you sure you want to mark " + artworks + " artwork" + artworks_s + " as bad prints?"
    );

    if (!agree) return;
    try {
      await api.artwork.markAsBad({ photos: badprintIds });
      fadeOutAndDeselectSelected();

      toastSuccess("Marked prints as bad");
      selectCounter();
    } catch (err: unknown) {
      alert((err as Error).message);
    }
  }
}

export async function removeFromCollection(): Promise<void> {
  const collectionIdEl = getDomElement<HTMLInputElement>("collection_id");
  if (!collectionIdEl?.value) return;
  const ids = selectedNumericIds();
  const artworks = ids.length;
  const artworks_s = artworks === 1 ? "" : "s";
  if (
    !confirm(
      "Are you sure you want to remove " +
        artworks +
        " artwork" +
        artworks_s +
        " from this collection?"
    )
  )
    return;
  try {
    await api.collections.removePhotos({ id: collectionIdEl.value, photos: ids });
    fadeOutAndDeselectSelected();

    toastSuccess(artworks + " artwork" + artworks_s + " removed from collection");
    selectCounter();

    const lbCountEl = getDomElement("lb-count");
    if (lbCountEl) {
      const newCount = parseInt(lbCountEl.innerHTML) - artworks;
      setText("lb-count", String(newCount));
      if (newCount === 1) {
        setText("lb-label", "artwork");
      } else {
        setText("lb-label", "artworks");
      }
    }
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

/**
 * Easy select with shift-click support
 */
export function easyselect(id: string, event: MouseEvent): void {
  if (event.shiftKey) {
    if (reordermode === 0) {
      const lmSelect = getDomElement<HTMLInputElement>("lm_select");
      if (lmSelect?.value === "1") {
        const selectedEl = getDomElement<HTMLInputElement>("selected-" + id);
        if (selectedEl?.value === "0") {
          setSelectIcon(id, true);
          selectedEl.value = "1";
          selectCounter();
        }
      }
    } else {
      const checkbox = getDomElement<HTMLInputElement>("reorder-select-input-" + id);
      if (checkbox && !checkbox.checked) {
        checkbox.checked = true;
        enterReorderMode();
      }
    }
  }
}

/**
 * Manual select/deselect item
 */
export function manualselect(id: string): void {
  const selectedEl = getDomElement<HTMLInputElement>("selected-" + id);
  if (selectedEl?.value === "1") {
    setSelectIcon(id, false);
    selectedEl.value = "0";
  } else {
    setSelectIcon(id, true);
    if (selectedEl) selectedEl.value = "1";
  }
  selectCounter();
}

/**
 * Toggle lightbox selection
 */
export function toggleLightbox(lightbox_id: string, _pic_id: string): void {
  const stateEl = getDomElement<HTMLInputElement>("lb-state-" + lightbox_id);
  if (stateEl) setLightboxSelected(lightbox_id, stateEl.value === "2");
}

/**
 * Set one row's selection explicitly, rather than flipping it.
 *
 * "Select all" needs to force a state, not toggle each row — toggling would DESELECT
 * everything already ticked. The hidden `lb-state-<id>` input is the real selection: the
 * save path collects `.lightbox-toggles` inputs whose value is "1" straight out of the DOM,
 * so this writes that and keeps the icon in step with it.
 */
export function setLightboxSelected(lightbox_id: string, selected: boolean): void {
  const stateEl = getDomElement<HTMLInputElement>("lb-state-" + lightbox_id);
  if (!stateEl) return;
  const toggle = getDomElement("toggle-" + lightbox_id);
  stateEl.value = selected ? "1" : "2";
  toggle?.classList.remove(selected ? "fa-square" : "fa-check-square");
  toggle?.classList.add("far", selected ? "fa-check-square" : "fa-square");
}

/**
 * Toggle filter option
 */
export function toggleOption(option_id: string, filter_id: string): void {
  const filterTypeEl = getDomElement<HTMLInputElement>("filter-type-" + filter_id);
  if (filterTypeEl) {
    hide("footer");
    const filter_type = filterTypeEl.value;
    const optionEl = getDomElement<HTMLInputElement>("option-" + option_id);
    if (optionEl) {
      const filter_var = optionEl.value;
      if (filter_type === "select-menu") {
        for (const optEl of queryAll<HTMLElement>(".all-options-" + filter_id)) {
          const check_option_id = idSuffix(optEl, "option-");
          if (!check_option_id) continue;
          const checkFilterIdEl = getDomElement<HTMLInputElement>("filter-id-" + check_option_id);
          if (!checkFilterIdEl) continue;
          if (!getDomElement("filter-type-" + checkFilterIdEl.value)) continue;
          const checkOptionEl = getDomElement<HTMLInputElement>("option-" + check_option_id);
          if (checkOptionEl) checkOptionEl.value = "0";
          setSearchcache(getRandomInt(100000, 100000000000));
        }
        optionEl.value = "1";
      }
      if (filter_type === "single-select") {
        for (const optEl of queryAll<HTMLElement>(".all-options")) {
          const check_option_id = idSuffix(optEl, "option-");
          if (!check_option_id) continue;
          const checkFilterIdEl = getDomElement<HTMLInputElement>("filter-id-" + check_option_id);
          if (!checkFilterIdEl) continue;
          const check_filter_id = checkFilterIdEl.value;
          if (!getDomElement("filter-type-" + check_filter_id)) continue;
          if (filter_id !== check_filter_id) continue;
          const checkOptionEl = getDomElement<HTMLInputElement>("option-" + check_option_id);
          if (checkOptionEl?.value === "1") {
            checkOptionEl.value = "0";
            setSearchcache(getRandomInt(100000, 100000000000));
          }
        }

        if (filter_var === "0") {
          optionEl.value = "1";
          setSearchcache(getRandomInt(100000, 100000000000));
        }
      }
      if (filter_type === "multi-select") {
        if (filter_var === "1") {
          optionEl.value = "0";
          setSearchcache(getRandomInt(100000, 100000000000));
        } else {
          optionEl.value = "1";
          setSearchcache(getRandomInt(100000, 100000000000));
        }
      }
    }
  }
  const lmOptionsEl = getDomElement<HTMLInputElement>("lm_options");
  if (lmOptionsEl) lmOptionsEl.value = "";

  parseOptions();
}

/**
 * Clear all filters
 */
export function clearfilters(): void {
  // The print-size control is not an option hidden, so the loop below cannot reach it and
  // "Clear filters" used to leave a Minimum DPI/PPI filter applied — with its segment still
  // in the URL.
  clearPrintSize();
  for (const optEl of queryAll<HTMLElement>(".all-options")) {
    const option_id = idSuffix(optEl, "option-");
    if (!option_id) continue;
    const optionEl = getDomElement<HTMLInputElement>("option-" + option_id);
    if (optionEl) optionEl.value = "0";
  }
  const lmOptionsEl = getDomElement<HTMLInputElement>("lm_options");
  if (lmOptionsEl) lmOptionsEl.value = "";
  setSearchcache(getRandomInt(100000, 100000000000));

  parseOptions();
}

registerActions({
  selectToggle,
  selectCounter,
  deselectall,
  selectall,
  // Dispatched from Chrome.tsx as an object property (`"data-fn": "multi"`), not
  // as a `data-fn="multi"` attribute — a form that neither check-inline-js nor
  // the ViewActionName type sees. Do not remove this entry because "nothing
  // dispatches it": the bulk download/lightbox/transfer buttons do.
  multi,
  removeFromCollection,
  toggleOption,
  clearfilters,
});
